import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

/**
 * ApprovalsPanel — operator inbox for PENDING approval requests.
 *
 * Lists every request the logged-in operator can see:
 *   - their own requests (shown for status visibility)
 *   - requests on items they hold grant_permission for (approvable)
 *
 * Approve / Reject buttons appear only on requests the operator can
 * resolve (own requests always; others only if grant_permission). The
 * resolver decides — the frontend just guesses based on `is_self`
 * vs the requester. v1 keeps it simple: the buttons render for every
 * row + the BFF 403s if the operator can't actually resolve.
 */

export default function ApprovalsPanel() {
  const { operator } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch('/api/approvals', { credentials: 'same-origin' });
      if (!r.ok) {
        setError(`HTTP ${r.status}`);
        return;
      }
      const j = await r.json();
      setRows((j && j.entity) || []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const resolveOne = async (id, action) => {
    const reason = window.prompt(`Optional ${action} reason:`, '') ?? '';
    const r = await fetch(`/api/approvals/${id}/${action}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision_reason: reason }),
    });
    if (!r.ok) {
      const body = await r.text();
      window.alert(`${action} failed: HTTP ${r.status}\n${body}`);
      return;
    }
    refresh();
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Tasks</h2>
        <button onClick={refresh} style={{
          background: 'none', border: '1px solid #ccc', borderRadius: '3px',
          padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer',
        }}>refresh</button>
      </div>

      {loading && <div style={{ color: '#888' }}>Loading…</div>}
      {error && <div style={{ color: '#c00' }}>{error}</div>}

      {!loading && rows.length === 0 && (
        <div style={{ color: '#888', fontSize: '0.9rem' }}>
          Nothing waiting — no pending approval requests.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px' }}>Requester</th>
              <th style={{ padding: '6px 8px' }}>Task</th>
              <th style={{ padding: '6px 8px' }}>Item</th>
              <th style={{ padding: '6px 8px' }}>Justification</th>
              <th style={{ padding: '6px 8px' }}>Created</th>
              <th style={{ padding: '6px 8px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isSelf = operator && r.requester_operator_id === operator.id;
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px 8px' }}>
                    {isSelf ? <strong>you</strong> : `#${r.requester_operator_id}`}
                  </td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.task}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#555' }}>
                    {r.item_depth}={r.item_id}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{r.justification}</td>
                  <td style={{ padding: '6px 8px', color: '#888', fontSize: '0.75rem' }}>
                    {r.created_at && new Date(r.created_at).toLocaleString()}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <button onClick={() => resolveOne(r.id, 'approve')} style={{
                      background: '#2a7', color: '#fff', border: 'none',
                      borderRadius: '3px', padding: '3px 10px', fontSize: '0.75rem',
                      cursor: 'pointer', marginRight: 6,
                    }}>approve</button>
                    <button onClick={() => resolveOne(r.id, 'reject')} style={{
                      background: '#c44', color: '#fff', border: 'none',
                      borderRadius: '3px', padding: '3px 10px', fontSize: '0.75rem',
                      cursor: 'pointer',
                    }}>reject</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
